import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Checkbox from "@/components/Checkbox";
import Progress from "@/components/Progress";
import Radio from "@/components/Radio";
import SubSectionHeading from "@/components/SubSectionHeading";
import type { MailtoField } from "./committeeMailto";

export function GroupEmailLoadingStatus({ children }: { children: React.ReactNode }) {
  return (
    <p className="flex items-center gap-2 text-body-medium text-sys-brand-on-surface-variant" role="status">
      <span aria-hidden="true">
        <Progress size={20} color="currentColor" />
      </span>
      {children}
    </p>
  );
}

export function emailCountCopy(count: number): string {
  return count === 1 ? "1 email address" : `${count} email addresses`;
}

export function excludedCopy(count: number): string {
  return count === 1 ? "1 person has no usable email." : `${count} people have no usable email.`;
}

export type GroupEmailCheckboxItem = {
  id: string;
  label: string;
  disabled?: boolean;
};

type GroupEmailCheckboxListProps = {
  heading: string;
  items: GroupEmailCheckboxItem[];
  selectedIds: ReadonlySet<string>;
  onToggle(id: string): void;
  onSelectAll?(): void;
  selectAllLabel?: string;
  selectAllTestId?: string;
  selectAllAriaLabel?: string;
  showSelectAll?: boolean;
  listTestId?: string;
  /** Prefix for checkbox `name`/`id` so sibling lists with overlapping item ids stay unique. */
  namePrefix?: string;
  /** boxed = scrollable bordered list (page + committees); plain = flat checkboxes (sheet roles) */
  variant?: "boxed" | "plain";
  maxHeightClassName?: string;
  filterSlot?: React.ReactNode;
  footerSlot?: React.ReactNode;
  children?: React.ReactNode;
};

export function GroupEmailCheckboxList({
  heading,
  items,
  selectedIds,
  onToggle,
  onSelectAll,
  selectAllLabel,
  selectAllTestId,
  selectAllAriaLabel,
  showSelectAll = true,
  listTestId,
  namePrefix = "group-email-item",
  variant = "boxed",
  maxHeightClassName = "max-h-56 md:max-h-80",
  filterSlot,
  footerSlot,
  children
}: GroupEmailCheckboxListProps) {
  const listClassName =
    variant === "plain"
      ? "flex flex-col gap-3"
      : `flex ${maxHeightClassName} flex-col gap-3 overflow-y-auto rounded-lg border border-sys-brand-outline-variant p-3`;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <SubSectionHeading>{heading}</SubSectionHeading>
        {showSelectAll && items.length > 0 && onSelectAll && (
          <Button
            variant="text"
            onClick={onSelectAll}
            testId={selectAllTestId}
            ariaLabel={selectAllAriaLabel ?? selectAllLabel}
          >
            {selectAllLabel}
          </Button>
        )}
      </div>
      {filterSlot}
      <div className={listClassName} data-testid={listTestId}>
        {children}
        {items.map((item) => (
          <Checkbox
            key={item.id}
            name={`${namePrefix}-${item.id}`}
            label={item.label}
            checked={selectedIds.has(item.id)}
            disabled={item.disabled}
            onChange={() => onToggle(item.id)}
          />
        ))}
      </div>
      {footerSlot}
    </section>
  );
}

type GroupEmailRecipientStatusProps = {
  visible: boolean;
  loading: boolean;
  emailCount: number;
  excludedCount: number;
  truncated: boolean;
  truncatedTestId?: string;
};

export function GroupEmailRecipientStatus({
  visible,
  loading,
  emailCount,
  excludedCount,
  truncated,
  truncatedTestId = "group-email-recipients-truncated"
}: GroupEmailRecipientStatusProps) {
  if (!visible && !truncated) return null;

  return (
    <>
      {visible && (
        <p className="text-body-medium" role="status">
          {loading
            ? "Loading email addresses…"
            : emailCount > 0
              ? `${emailCountCopy(emailCount)}.${excludedCount > 0 ? ` ${excludedCopy(excludedCount)}` : ""}`
              : excludedCount > 0
                ? `No email addresses. ${excludedCopy(excludedCount)}`
                : "No email addresses."}
        </p>
      )}
      {truncated && (
        <Banner
          variant="warn"
          inline
          description="Not all email addresses loaded. You can still copy or open email with the addresses that loaded."
          testId={truncatedTestId}
        />
      )}
    </>
  );
}

type GroupEmailAddToFieldProps = {
  field: MailtoField;
  onChange(field: MailtoField): void;
  emailCount: number;
  namePrefix?: string;
};

export function GroupEmailAddToField({
  field,
  onChange,
  emailCount,
  namePrefix = "group-email-field"
}: GroupEmailAddToFieldProps) {
  return (
    <section className="flex flex-col gap-4">
      <SubSectionHeading>Add To</SubSectionHeading>
      <Radio
        id={`${namePrefix}-to`}
        name={namePrefix}
        label="To"
        checked={field === "to"}
        onChange={(checked) => {
          if (checked) onChange("to");
        }}
      />
      <Radio
        id={`${namePrefix}-cc`}
        name={namePrefix}
        label="Cc"
        checked={field === "cc"}
        onChange={(checked) => {
          if (checked) onChange("cc");
        }}
      />
      <Radio
        id={`${namePrefix}-bcc`}
        name={namePrefix}
        label={<span id={`${namePrefix}-bcc-label`}>Bcc</span>}
        aria-labelledby={`${namePrefix}-bcc-label`}
        supportingText="Hides addresses from other recipients."
        checked={field === "bcc"}
        onChange={(checked) => {
          if (checked) onChange("bcc");
        }}
      />
      {(field === "to" || field === "cc") && (
        <Banner
          variant="warn"
          inline
          description={`Everyone on this email will see the addresses (${emailCount}).`}
          testId={`${namePrefix}-visible-addresses-warning`}
        />
      )}
    </section>
  );
}

type GroupEmailActionsProps = {
  onCopy(): void;
  onOpen(): void;
  onCancel?(): void;
  disabled: boolean;
  copying: boolean;
  showCancel?: boolean;
  copyTestId?: string;
  openTestId?: string;
  cancelTestId?: string;
};

export function GroupEmailActions({
  onCopy,
  onOpen,
  onCancel,
  disabled,
  copying,
  showCancel = false,
  copyTestId = "group-email-copy",
  openTestId = "group-email-open",
  cancelTestId = "group-email-cancel"
}: GroupEmailActionsProps) {
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {showCancel && onCancel && (
        <Button variant="text" onClick={onCancel} disabled={copying} testId={cancelTestId}>
          Cancel
        </Button>
      )}
      <Button variant="text" onClick={onCopy} disabled={disabled} testId={copyTestId}>
        Copy Addresses
      </Button>
      <Button variant="filled" onClick={onOpen} disabled={disabled} testId={openTestId}>
        Open Email
      </Button>
    </div>
  );
}
